import { UnifiedRoute } from '../services/firebase/routes'

/**
 * Format route location for display
 * Returns formatted string like "Melbourne, VIC, AU"
 */
export function formatRouteLocation(route: UnifiedRoute): string {
  // Check route-level location first (new optimized approach)
  let location: any = route.location
  
  if (!location) {
    // Legacy fallbacks
    if ((route as any)._statsOnlySegments && (route as any)._statsOnlySegments.length > 0) {
      location = (route as any)._statsOnlySegments[0].location
    } else if (route.routes && route.routes.length > 0) {
      location = route.routes[0].location
    }
  }
  
  if (!location) {
    return 'Unknown Location'
  }

  const { city = '', state = '', country = '', region = '' } = location

  // State abbreviations (from HomePage)
  const stateAbbreviations: { [key: string]: string } = {
    'Victoria': 'VIC',
    'New South Wales': 'NSW',
    'Queensland': 'QLD',
    'Western Australia': 'WA',
    'South Australia': 'SA',
    'Tasmania': 'TAS',
    'Northern Territory': 'NT',
    'Australian Capital Territory': 'ACT'
  }

  // Country abbreviations
  const countryAbbreviations: { [key: string]: string } = {
    'Australia': 'AU',
    'New Zealand': 'NZ',
    'United States': 'USA',
    'United Kingdom': 'UK'
  }

  const stateAbbr = stateAbbreviations[state] || state.substring(0, 3).toUpperCase()
  const countryAbbr = countryAbbreviations[country] || country.substring(0, 2).toUpperCase()

  // Build full location string: "Region, State (abbr), Country (abbr)"
  return `${region || city}, ${stateAbbr}, ${countryAbbr}`
}

/**
 * Calculate unpaved percentage from route segments
 */
export function calculateUnpavedPercentage(route: UnifiedRoute): number {
  // First check if we have aggregated surface breakdown at route level (new optimized approach)
  if (route.surfaceBreakdown?.unpaved?.percentage !== undefined) {
    return Math.round(route.surfaceBreakdown.unpaved.percentage)
  }
  
  // Legacy fallback: Use _statsOnlySegments if available (from old listRoutes), otherwise use full routes
  const segments = (route as any)._statsOnlySegments || route.routes || []
  
  if (segments.length === 0) return 0

  let totalDistance = 0
  let totalUnpavedDistance = 0

  segments.forEach((segment: any) => {
    const segmentDistance = segment.stats?.distance || 0
    totalDistance += segmentDistance

    if (segment.stats?.surfaceBreakdown?.unpaved?.percentage !== undefined) {
      totalUnpavedDistance += segmentDistance * (segment.stats.surfaceBreakdown.unpaved.percentage / 100)
    } else if (segment.stats?.unpaved !== undefined) {
      totalUnpavedDistance += segmentDistance * (segment.stats.unpaved / 100)
    }
  })

  if (totalDistance === 0) return 0
  return Math.round((totalUnpavedDistance / totalDistance) * 100)
}

/**
 * Get display name for route type
 */
export function getRouteTypeDisplay(routeType: string | undefined): string {
  const typeMap: { [key: string]: string } = {
    'single': 'Single Route',
    'bikepacking': 'Bikepacking',
    'event': 'Event',
    'permanent': 'Permanent',
    'rail_trail': 'Rail Trail',
    'railtrail': 'Rail Trail',
    'rail trail': 'Rail Trail',  // lowercase with space
    'Rail Trail': 'Rail Trail',
    'bike_path': 'Bike Path',
    'bikepath': 'Bike Path',
    'bike path': 'Bike Path',  // lowercase with space
    'Bike Path': 'Bike Path',
    'bike_path_route': 'Bike Path Route',
    'bikepathroute': 'Bike Path Route',
    'bike path route': 'Bike Path Route',  // lowercase with space
    'Bike Path Route': 'Bike Path Route'
  }
  return typeMap[routeType || ''] || 'Single Route'
}

/**
 * Format surface description (e.g., "75% paved")
 */
export function formatSurfaceDescription(route: UnifiedRoute): string {
  const unpavedPercentage = calculateUnpavedPercentage(route)
  const pavedPercentage = 100 - unpavedPercentage
  return `${pavedPercentage}% paved`
}

/**
 * Get organization name for display
 */
export function getOrganizationName(route: UnifiedRoute): string {
  return route.submission?.clubName || 'Personal Route'
}

/**
 * Get route stage count
 */
export function getRouteStageCount(route: UnifiedRoute): number {
  return route.routeCount || route.routes?.length || 1
}

/**
 * Format stage count display (e.g., "3 stages" or "1 stage")
 */
export function formatStageCount(route: UnifiedRoute): string {
  const count = getRouteStageCount(route)
  return `${count} ${count === 1 ? 'stage' : 'stages'}`
}

/**
 * Get total distance from UnifiedRoute
 */
export function getTotalDistance(route: UnifiedRoute): number {
  return route.distance || 0
}

/**
 * Get total elevation gain
 */
export function getTotalElevationGain(route: UnifiedRoute): number {
  return route.elevation?.up || 0
}

/**
 * Get total elevation loss
 */
export function getTotalElevationLoss(route: UnifiedRoute): number {
  return route.elevation?.down || 0
}

/**
 * Extract region from route location
 */
export function getRouteRegion(route: UnifiedRoute): string {
  // Check route-level location first (new optimized approach)
  if (route.location) {
    return route.location.region || route.location.city || ''
  }
  // Legacy fallbacks
  if ((route as any)._statsOnlySegments && (route as any)._statsOnlySegments.length > 0) {
    const firstSegment = (route as any)._statsOnlySegments[0]
    return firstSegment.location?.region || firstSegment.location?.city || ''
  }
  // Then check regular routes
  if (!route.routes || route.routes.length === 0) return ''
  const firstRoute = route.routes[0]
  return firstRoute.location?.region || firstRoute.location?.city || ''
}

/**
 * Extract state from route location
 */
export function getRouteState(route: UnifiedRoute): string {
  // Check route-level location first (new optimized approach)
  if (route.location) {
    return route.location.state || ''
  }
  // Legacy fallbacks
  if ((route as any)._statsOnlySegments && (route as any)._statsOnlySegments.length > 0) {
    const firstSegment = (route as any)._statsOnlySegments[0]
    return firstSegment.location?.state || ''
  }
  // Then check regular routes
  if (!route.routes || route.routes.length === 0) return ''
  return route.routes[0].location?.state || ''
}

/**
 * Get coordinates for map marker
 */
export function getRouteCoordinates(route: UnifiedRoute): [number, number] | null {
  // Check if this is a transformed embed route with firebaseRoute property
  if ((route as any).firebaseRoute) {
    const firebaseRoute = (route as any).firebaseRoute
    
    // Try coordinates on the nested firebaseRoute
    if (firebaseRoute.coordinates && firebaseRoute.coordinates.length > 0) {
      return firebaseRoute.coordinates[0]
    }
    
    // Try routes array on the nested firebaseRoute
    if (firebaseRoute.routes && firebaseRoute.routes.length > 0 && firebaseRoute.routes[0].coordinates) {
      return firebaseRoute.routes[0].coordinates[0]
    }
  }
  
  // Try the backward compatibility coordinates first
  if (route.coordinates && route.coordinates.length > 0) {
    return route.coordinates[0]
  }
  
  // Otherwise get from first route segment
  if (route.routes && route.routes.length > 0 && route.routes[0].coordinates) {
    return route.routes[0].coordinates[0]
  }
  
  return null
}

/**
 * Get route description (overview)
 */
export function getRouteDescription(route: UnifiedRoute): string {
  return route.description || (route as any).masterRouteDescription || ''
}